import { useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import { useDarkMode } from "../contexts/DarkModeContext";
import { useAgentConfig } from "../contexts/AgentConfigContext";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../components/Toast";
import { Download, ChevronDown } from 'lucide-react';
import Tooltip from "../common_jsx/Tooltip";

const DOWNLOAD_SSE_IDLE_TIMEOUT_MS = 30000;

function Options({ id, sessionId, sourceKey, isMultiSource = false, tooltipText = "Download", disabled = false }) {
  const { isDarkMode } = useDarkMode();
  const { config } = useAgentConfig();
  const { getUserIdDiscover } = useAuth();
  const toast = useToast();
  const [isDownloading, setIsDownloading] = useState(false);
  const [menuPos, setMenuPos] = useState(null); // null = closed
  const [hoveredScope, setHoveredScope] = useState(null);
  const [submenuTopOffset, setSubmenuTopOffset] = useState(0);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const scopeRowsRef = useRef({});
  const scopeHoverTimer = useRef(null);
  const sseAbortRef = useRef(null);

  const isOpen = menuPos !== null;


  const openMenu = () => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setMenuPos({
      bottom: window.innerHeight - r.top + 4,
      right: window.innerWidth - r.right,
    });
  };

  const closeMenu = () => {
    setMenuPos(null);
    setHoveredScope(null);
  };

  // Abort any active SSE subscription on unmount and clear hover timer
  useEffect(() => {
    return () => {
      sseAbortRef.current?.abort();
      if (scopeHoverTimer.current) clearTimeout(scopeHoverTimer.current);
    };
  }, []);

  // Close menu when clicking outside or scrolling
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target) &&
        (!menuRef.current || !menuRef.current.contains(e.target))
      ) closeMenu();
    };
    document.addEventListener('mousedown', handleClick);
    window.addEventListener('scroll', closeMenu, true);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      window.removeEventListener('scroll', closeMenu, true);
    };
  }, [isOpen]);

  const openDownloadUrl = (url) => {
    try {
      const parsed = new URL(url);
      if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
        toast.error('Download link uses an unsupported URL protocol.');
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      toast.error('Received an invalid download link from the server.');
    }
  };

  const handleDownload = async (format, scope = 'current') => {
    if (isDownloading) return;
    closeMenu();
    setIsDownloading(true);
    try {
      const userId = getUserIdDiscover();
      const body = {
        message_id: id,
        session_id: sessionId,
        user_id: userId,
        format,
      };
      if (scope === 'all') {
        body.source_key = 'all';
      } else if (sourceKey) {
        body.source_key = sourceKey;
      }

      const response = await fetch(
        `${config.serviceUrl}/agents/${config.agentId}/download`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${config.apiKey}`,
          },
          body: JSON.stringify(body),
        }
      );

      const contentType = response.headers.get('content-type') || '';
      const normalizedContentType = contentType.toLowerCase();
      const disposition = response.headers.get('content-disposition') || '';
      const isJson = normalizedContentType.includes('application/json');
      const isFile = !isJson && (
        disposition.toLowerCase().includes('attachment') ||
        normalizedContentType.includes('text/csv') ||
        normalizedContentType.includes('spreadsheetml') ||
        normalizedContentType.includes('application/zip') ||
        normalizedContentType.includes('application/octet-stream')
      );

      if (isFile) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Handles both quoted (filename="foo.csv") and unquoted (filename=foo.csv),
        // stopping at delimiters so trailing params like ; size=... are ignored.
        const match = disposition.match(/filename="([^"]+)"|filename=([^";\s]+)/i);
        const inferredExt = normalizedContentType.includes('application/zip')
          ? 'zip'
          : normalizedContentType.includes('spreadsheetml')
            ? 'xlsx'
            : format;
        a.download = (match && (match[1] || match[2])) || `download.${inferredExt}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } else {
        const data = await response.json();
        if (data.code !== 0) {
          toast.error(data.message || 'Download failed. Please try again.');
        } else if (data.data?.status === 'done' && data.data?.download_url) {
          openDownloadUrl(data.data.download_url);
        } else if (data.data?.status === 'queued' && data.data?.job_id) {
          toast.info("Preparing file. You’ll be notified when it’s ready.");
          subscribeToDownload(config.agentId, data.data.job_id);
        } else {
          toast.error('Download failed. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Download failed. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const subscribeToDownload = async (agentId, jobId) => {
    sseAbortRef.current?.abort();
    const controller = new AbortController();
    sseAbortRef.current = controller;
    let reader = null;
    let timedOut = false;
    let timeoutId;
    const cancelReader = async () => {
      if (!reader) return;
      try {
        await reader.cancel();
      } catch {
        // Stream may already be closed/aborted.
      } finally {
        reader = null;
      }
    };
    const resetIdleTimeout = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, DOWNLOAD_SSE_IDLE_TIMEOUT_MS);
    };
    resetIdleTimeout();

    try {
      const resp = await fetch(
        `${config.serviceUrl}/agents/${agentId}/download-subscribe/${jobId}`,
        {
          headers: { 'Authorization': `Bearer ${config.apiKey}` },
          signal: controller.signal,
        }
      );
      if (!resp.ok || !resp.body) {
        toast.error('Failed to subscribe to download updates.');
        return;
      }

      reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        resetIdleTimeout();
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split(/\r?\n/);
        buffer = lines.pop(); // keep incomplete line in buffer

        for (const line of lines) {
          if (!line.startsWith('data:')) continue;
          const payload = line.slice(5).trim();
          if (payload === '[DONE]') {
            await cancelReader();
            return;
          }

          try {
            const event = JSON.parse(payload);
            if (event.status === 'done' && event.download_url) {
              toast.success('Your file is ready!', 0, {
                label: 'Download',
                onClick: () => openDownloadUrl(event.download_url),
              });
              await cancelReader();
              return;
            }
            if (event.status === 'failed') {
              toast.error(event.message || 'Download failed on server.');
              await cancelReader();
              return;
            }
            if (event.type === 'error') {
              toast.error(event.message || 'Download subscription ended with an error.');
              await cancelReader();
              return;
            }
          } catch (parseErr) {
            console.warn('SSE: failed to parse event payload:', payload, parseErr);
          }
        }
      }
    } catch (err) {
      if (timedOut) {
        toast.error('Timed out waiting for file preparation. Please try downloading again.');
      } else if (err.name !== 'AbortError') {
        console.error('SSE download subscribe error:', err);
        toast.error('Lost connection to download updates.');
      }
    } finally {
      clearTimeout(timeoutId);
      await cancelReader();
      controller.abort();
      if (sseAbortRef.current === controller) sseAbortRef.current = null;
    }
  };

  const btnBase = isDarkMode
    ? 'text-slate-400 hover:text-slate-200 hover:bg-slate-700'
    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100';
  const btnDisabled = isDarkMode ? 'text-slate-400 opacity-50 cursor-not-allowed' : 'text-gray-500 opacity-50 cursor-not-allowed';

  const isDisabled = disabled || isDownloading;

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown if it becomes disabled while open (e.g. download starts)
  useEffect(() => {
    if (isDisabled && dropdownOpen) setDropdownOpen(false);
  }, [isDisabled, dropdownOpen]);

   const sharedMenuItemCls = `w-full px-4 py-2 text-xs text-left whitespace-nowrap transition-colors ${
    isDarkMode ? 'text-slate-200 hover:bg-slate-700' : 'text-gray-700 hover:bg-gray-50'
  }`;

  const scopeRowCls = (scope) => "w-full flex items-center justify-between gap-6 px-4 py-2 text-xs whitespace-nowrap transition-colors cursor-pointer " + (
    hoveredScope === scope
      ? (isDarkMode ? 'bg-slate-600 text-white font-medium' : 'bg-blue-50 text-blue-700 font-medium')
      : (isDarkMode ? 'text-slate-200' : 'text-gray-700')
  );
  const menu = isOpen && createPortal(
    <div
      ref={menuRef}
      style={{ position: 'fixed', bottom: menuPos.bottom, right: menuPos.right }}
      className={`rounded-lg shadow-lg border z-[9999] overflow-visible min-w-max ${
        isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-200'
      }`}
    >
      {isMultiSource ? (
        <div className="relative">
          {[
            { scope: 'current', label: 'Current Output' },
            { scope: 'all', label: 'All Outputs' },
          ].map(({ scope, label }, idx) => (
             <div
              key={scope}
              onClick={() => setHoveredScope(prev => prev === scope ? null : scope)}
              onMouseEnter={() => {
                clearTimeout(scopeHoverTimer.current);
                setHoveredScope(scope);
                // Calculate offset of hovered row relative to parent container
                if (scopeRowsRef.current[scope]) {
                  const rowEl = scopeRowsRef.current[scope];
                  const parentEl = rowEl.parentElement;
                  if (parentEl) {
                    const offset = rowEl.offsetTop - parentEl.offsetTop;
                    setSubmenuTopOffset(offset);
                  }
                }
              }}
              onMouseLeave={() => {
                scopeHoverTimer.current = setTimeout(() => setHoveredScope(null), 100);
              }}
            >
              {idx > 0 && (
                <div className={`mx-3 border-t ${isDarkMode ? 'border-slate-600' : 'border-gray-100'}`} />
              )}
              <div
                ref={(el) => {
                  if (el) scopeRowsRef.current[scope] = el;
                }}
                className={scopeRowCls(scope)}
              >
                {label}
                <ChevronDown className="w-3 h-3 -rotate-90 opacity-50" />
              </div>
            </div>
          ))}

          {/* Submenu — positioned next to the hovered row */}
          {hoveredScope && (
            <div
              className={`absolute left-full ml-0 rounded-lg shadow-lg border z-[9999] overflow-hidden min-w-max ${
                isDarkMode ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-200'
              }`}
              style={{ top: `${submenuTopOffset}px` }}
              onMouseEnter={() => clearTimeout(scopeHoverTimer.current)}
              onMouseLeave={() => {
                scopeHoverTimer.current = setTimeout(() => setHoveredScope(null), 100);
              }}
            >
              <button className={sharedMenuItemCls} onClick={() => handleDownload('csv', hoveredScope)}>CSV</button>
              <button className={sharedMenuItemCls} onClick={() => handleDownload('xlsx', hoveredScope)}>Excel</button>
            </div>
          )}
        </div>
      ) : (
        <>
          <button className={sharedMenuItemCls} onClick={() => handleDownload('csv', 'current')}>CSV</button>
          <button className={sharedMenuItemCls} onClick={() => handleDownload('xlsx', 'current')}>Excel</button>
        </>
      )}
    </div>,
    document.body
  );

  return (
    <div className="relative inline-flex items-center" ref={dropdownRef}>
      {/* Main download icon button with styled tooltip */}
      <Tooltip text={isDownloading ? "Downloading..." : (disabled ? "Download disabled" : tooltipText)} isDarkMode={isDarkMode}>
        <button
          className={`flex items-center gap-0.5 p-2 rounded-lg transition-colors ${
            isDisabled ? btnDisabled : btnBase
          }`}
          onClick={() => isOpen ? closeMenu() : openMenu()}
          disabled={isDisabled}
          ref={btnRef}
          aria-label="Download options"
          aria-haspopup="menu"
          aria-expanded={isOpen}
        >
          {isDownloading ? (
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : (
            <Download className="w-4 h-4" />
          )}
          <ChevronDown className="w-3 h-3" />
        </button>
      </Tooltip>
      {menu}
    </div>
  );
}

Options.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  sessionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  sourceKey: PropTypes.string,
  isMultiSource: PropTypes.bool,
  tooltipText: PropTypes.string,
  disabled: PropTypes.bool,
};

export default Options;
